import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { HelpCircle, X } from 'lucide-react';
import { AYUDA_INVENTARIO } from '../ayuda/ayudaInventario';

/** Resalta **palabra** como <strong> dentro de un texto. */
function renderTexto(texto) {
  return String(texto).split(/(\*\*[^*]+\*\*)/g).map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**')) {
      return <strong key={i} className="font-semibold text-slate-700">{p.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

/**
 * Botón "Ayuda" que abre un drawer con la guía rápida de la sección.
 * Uso: <GuiaRapida seccion="dashboard" />  (dashboard, tipos, compras, historial, ajustes)
 *
 * El drawer se renderiza en un portal a document.body para que el `fixed`
 * se ancle al viewport (y no a un contenedor con transform) y cubra toda la
 * altura, por encima del navbar — como el mockup.
 */
export default function GuiaRapida({ seccion }) {
  const [open, setOpen] = useState(false);
  const data = AYUDA_INVENTARIO[seccion];

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!data) return null;

  // Variante "respeta el navbar": el contenedor del drawer arranca a
  // 48 px del top (altura aproximada del navbar superior fijo del proyecto)
  // y se queda con z-index 999, por debajo del navbar (z 1050). Así la
  // campana y el avatar del navbar siguen visibles y la cabecera del
  // drawer ya no queda recortada.
  const drawer = (
    <div className="fixed inset-x-0 bottom-0 top-12 z-[999]">
      <div className="absolute inset-0 bg-black/25" onClick={() => setOpen(false)} />
      <aside className="absolute top-0 right-0 bottom-0 w-[480px] max-w-[90vw] bg-white shadow-2xl flex flex-col">
        <div className="bg-[hsl(var(--brand-dark))] text-white px-5 py-4 flex items-start justify-between shrink-0">
          <div>
            <h2 className="text-base font-semibold flex items-center gap-2">
              <HelpCircle className="w-4 h-4" />
              Guía rápida · {data.titulo}
            </h2>
            {data.intro && <p className="text-xs text-white/70 mt-0.5">{data.intro}</p>}
          </div>
          <button type="button" onClick={() => setOpen(false)} className="text-white/80 hover:text-white" aria-label="Cerrar">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Cada paso va en una "card" suave para que se diferencien entre
            sí: sin las cards, los pasos se confundían en el blanco. */}
        <div className="p-5 space-y-3 overflow-y-auto flex-1 min-h-0 bg-slate-50/50">
          {data.pasos.map((paso, i) => (
            <div
              key={i}
              className="flex gap-3 bg-white border border-slate-200 rounded-lg p-3 shadow-sm"
            >
              <span className="shrink-0 w-7 h-7 rounded-full bg-[hsl(var(--brand-dark))] text-white text-sm font-semibold flex items-center justify-center">
                {i + 1}
              </span>
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800">
                  {paso.titulo}
                  {paso.admin && (
                    <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">Admin</span>
                  )}
                </p>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">{renderTexto(paso.texto)}</p>
              </div>
            </div>
          ))}
          {data.pasos.some((p) => p.admin) && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
              Los pasos marcados
              {' '}<span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800">Admin</span>{' '}
              son de configuración; según tu rol verás esas secciones o no.
            </div>
          )}
        </div>
      </aside>
    </div>
  );

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-[hsl(var(--brand-light))] border border-slate-300 rounded-lg px-3 py-2 hover:bg-slate-50"
      >
        <HelpCircle className="w-4 h-4" />
        Ayuda
      </button>
      {open && createPortal(drawer, document.body)}
    </>
  );
}
